import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import useEventListener from '../../../hooks/useEventListener';
import { getWindowSizeToParam } from '../../../hooks/usePluginAppViewport';
import usePluginHandler from '../../../hooks/usePluginHandler';
import { isCalledByParent } from '../../../models/caller';
import { ParentCallerUrlParam, ParentResolution, PluginMessageTypes } from '../../../models/constants';
import MessagesPlugin from '../../../models/messages';
import { getQueryParamsFromObject, getUrlParams, setLocationHash } from '../../../utils/navigation';
import { versionCompare } from '../../../utils/version';

function getIframeHash(routerPathBaseParam, hash) {
  return hash ? hash.replace(routerPathBaseParam, '') : '';
}

function getHashTag(src) {
  return src.indexOf('#') < 0 ? '#' : '';
}

function getResolution() {
  const urlParams = getUrlParams();
  // passo al children il parametro passato da un eventuale altro parent, oppure la dimensione della window corrente
  return urlParams[ParentResolution] || getWindowSizeToParam();
}

export default function useRoutingHandler(src, updateHash, srcParams, inMemoryRouting) {
  const [initialSrc, setInitialSrc] = useState(null);
  const [routerPathBase, setRouterPathBase] = useState('');
  const initialized = useRef(false);
  const currentHash = useRef(null);
  const appLoaded = useRef(false);
  const reloadMissing = useRef(false);

  const hashTag = src.indexOf('#') < 0 ? '#' : '';

  const setInitialSrcAndHash = useCallback((routerPathBaseParam, hash) => {
    const queryParms = getQueryParamsFromObject({ ...srcParams, [ParentResolution]: getResolution() });
    const newHash = getIframeHash(routerPathBaseParam, hash);
    const newInitialSrc = `${src}${getHashTag(src)}${updateHash ? newHash : ''}?${ParentCallerUrlParam}=${MessagesPlugin.getIdReceiver()}${queryParms}`;
    setInitialSrc(newInitialSrc);
    if (updateHash) {
      currentHash.current = newHash;
      if (!inMemoryRouting) {
        setLocationHash(hashTag + hash);
      }
    }
    return newInitialSrc;
  }, [srcParams, src, updateHash, inMemoryRouting, hashTag]);

  const match = useRouteMatch();
  const location = useLocation();

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    // prendo la parte di dettaglio da passare all'iframe
    // oltre al route path
    const routerPath = match.path;
    const newRouterPathBase = routerPath.substring(0, routerPath.indexOf(':') - 1);
    const { pathname } = location;
    setRouterPathBase(newRouterPathBase);
    setInitialSrcAndHash(newRouterPathBase, pathname);
  }, [location, match.path, setInitialSrcAndHash]);

  /**
   * Evento di cambiamento hash dal child (quindi senza routerPathBase)
   */
  const eventoChangeHash = useCallback(({ initialVersion, payload: subHash }) => {
    if (!updateHash) return;

    // se è stato inviato questo evento da una versione superiore alla 1.6.2 allora
    // lascio la gestione all'evento nuovo CHANGE_HASH2 (che arriverà parallelamente)
    if (versionCompare(initialVersion, '1.6.2') >= 0) return;

    const hash = routerPathBase + (subHash === '/' ? '' : subHash);
    // se inserito in un parent rilancio il cambiamento di hash
    // e gli lascio l'onere di gestire l'history
    if (isCalledByParent()) {
      MessagesPlugin.postMessageToParent(
        PluginMessageTypes.CHANGE_HASH, hash,
      );
      return;
    }

    currentHash.current = hash;
    setLocationHash(hashTag + hash);
  }, [hashTag, routerPathBase, updateHash]);

  /**
   * Evento di cambiamento hash dal child (quindi senza routerPathBase) (versione avanzata con replace)
   */
  const eventoChangeHash2 = useCallback(({ payload: { path, action } }) => {
    if (!updateHash) return;

    const hash = routerPathBase + (path === '/' ? '' : path);
    // se inserito in un parent rilancio il cambiamento di hash
    // e gli lascio l'onere di gestire l'history
    if (isCalledByParent()) {
      // mando il vecchio messaggio per retrocompatibilità
      MessagesPlugin.postMessageToParent(
        PluginMessageTypes.CHANGE_HASH, hash,
      );
      MessagesPlugin.postMessageToParent(
        PluginMessageTypes.CHANGE_HASH2, { action, path: hash },
      );
      return;
    }
    currentHash.current = hash;
    setLocationHash(hashTag + hash, action === 'REPLACE');
  }, [hashTag, routerPathBase, updateHash]);

  const handleMessages = useCallback((message) => {
    switch (message.type) {
      case PluginMessageTypes.LOADED:
        // conferma caricamento pagina nell'iframe
        appLoaded.current = true;
        break;
      case PluginMessageTypes.CHANGE_HASH:
        // viene comunicato un cambiamento hash nell'iframe
        eventoChangeHash(message);
        break;
      case PluginMessageTypes.CHANGE_HASH2:
        // viene comunicato un cambiamento hash nell'iframe
        eventoChangeHash2(message);
        break;
      default: {
        break;
      }
    }
  }, [eventoChangeHash, eventoChangeHash2]);
  usePluginHandler(handleMessages);

  const history = useHistory();
  /**
   * Evento di cambio hash sul parent (quindi compreso routerPathBase)
   */
  const onHashChange = useCallback(() => {
    const newHashWithParams = window.location.hash.replace('#', '');
    const newHash = newHashWithParams.split('?')[0];
    if (currentHash.current != null && currentHash.current !== newHash) {
      const newInitialSrc = setInitialSrcAndHash(routerPathBase, newHash);
      // nel seguente replace non devono essere esclusi eventuali parametri query string
      history.replace(newHashWithParams);
      MessagesPlugin.postMessageToChilds(
        PluginMessageTypes.RELOAD, newInitialSrc,
      );
    }
  }, [history, routerPathBase, setInitialSrcAndHash]);

  /**
   * Si sottoscrive all'evento di hash change.
   * Se è richiesto una hash change prima dell'avvenuto caricamento, lo eseguie appena il child è stato caricato.
   */

  const handleHashchangeEvent = useCallback(() => {
    if (!appLoaded.current) {
      reloadMissing.current = true;
    } else {
      onHashChange();
    }
  }, [onHashChange]);

  useEventListener('hashchange', handleHashchangeEvent, false, 'routingHandler');

  const onLoaded = useCallback(() => {
    // se durante il caricamento sono stati effettuati cambi di hash, allora inoltro l'evento
    if (reloadMissing.current) {
      onHashChange();
    }
  }, [onHashChange]);

  return { initialSrc, onLoaded };
}
